/**
 * A tab opened before a deploy still runs the old build. Each page's code is a
 * separate file with a content hash in its name, and a deploy replaces those
 * files, so the first visit to a page the tab has not loaded yet asks for a file
 * that no longer exists ("Failed to fetch dynamically imported module ...") and
 * the page showed "Something went wrong". Reload once instead: the tab picks up
 * the new build and lands on the page it asked for. The reload goes through
 * utils/appUpdate.ts (loadNewBuildNow), so it looks like the quiet update: the
 * page area empties under the rail and header, a waiting new service worker
 * takes over first, and the next page starts under a copy of the frame
 * (utils/updateShell.ts) instead of a white page and the splash screen.
 *
 * The reload is remembered for 10 seconds in sessionStorage, so a file that is
 * genuinely unreachable (an outage) shows the error instead of reloading in a
 * loop. Without sessionStorage there is no guard, so no automatic reload.
 *
 * Within one page, once a reload has started every later missing-file error waits
 * for it too. One stale dialog fails twice in a row (Vite's preload of its CSS,
 * then its module): the first started the reload, the second used to hit the
 * 10-second guard and throw, so the toolbar crashed a moment before the reload
 * landed (Test Kitchen #14, ReportProblemDialog, 2026-10-14).
 */
import { lazy, type ComponentType, type LazyExoticComponent } from 'react';
import { loadNewBuildNow } from './appUpdate';

const KEY = 'dietech.staleBuildReloadAt';
const GUARD_MS = 10_000;

// This page already started a reload; module state, so a fresh page load clears it.
let reloadStarted = false;

/** Test hook: the state a freshly loaded page starts with. */
export function resetStaleBuildReloadForTests(): void {
  reloadStarted = false;
}

const CHUNK_ERROR =
  /Failed to fetch dynamically imported module|Importing a module script failed|error loading dynamically imported module|Unable to preload CSS|Loading (CSS )?chunk [\w-]+ failed|ChunkLoadError/i;

export function isChunkLoadError(err: unknown): boolean {
  const message = err instanceof Error ? `${err.name}: ${err.message}` : String(err ?? '');
  return CHUNK_ERROR.test(message);
}

/**
 * Starts one reload unless one started in the last 10 s, and returns whether the page is now
 * reloading: true as well when this page already started one, so the caller waits for it.
 */
export function reloadForNewBuild(
  storage: Pick<Storage, 'getItem' | 'setItem'> | null = safeSessionStorage(),
  now: number = Date.now(),
  reload: () => void = () => { loadNewBuildNow(); },
): boolean {
  if (reloadStarted) return true;
  if (!storage) return false;
  try {
    const last = Number(storage.getItem(KEY) ?? 0);
    if (Number.isFinite(last) && now - last < GUARD_MS) return false;
    storage.setItem(KEY, String(now));
  } catch {
    return false;
  }
  reloadStarted = true;
  reload();
  return true;
}

/**
 * Loads a page's module for React.lazy. A replaced file shows up two ways: the import rejects
 * (handled here), or Vite's preload of its dependencies fails first, installStaleBuildReload
 * prevents that event's default and the import RESOLVES to undefined. React.lazy then read
 * `.default` of undefined and the page crashed ("Cannot read properties of undefined (reading
 * 'default')", Test Kitchen #9 on /menucycle, 2026-10-09) a moment before the reload landed.
 * Both mean a reload is under way, so keep the loading fallback up.
 */
export function loadForLazy<M>(factory: () => Promise<M>): Promise<M> {
  // Never settles: the tab is reloading.
  const reloading = () => new Promise<M>(() => {});
  return factory().then(
    (mod) => mod ?? reloading(),
    (err: unknown) => {
      if (isChunkLoadError(err) && reloadForNewBuild()) return reloading();
      throw err;
    },
  );
}

/** React.lazy that reloads the tab once when the page's file is from a replaced build. */
// eslint-disable-next-line @typescript-eslint/no-explicit-any -- same constraint as React.lazy
export function lazyWithReload<T extends ComponentType<any>>(factory: () => Promise<{ default: T }>): LazyExoticComponent<T> {
  return lazy(() => loadForLazy(factory));
}

/** Vite reports a failed preload of a page's dependencies with this event. */
export function installStaleBuildReload(target: Pick<Window, 'addEventListener'> = window): void {
  target.addEventListener('vite:preloadError', (event: Event) => {
    if (reloadForNewBuild()) event.preventDefault();
  });
}

function safeSessionStorage(): Storage | null {
  try {
    return typeof window !== 'undefined' ? window.sessionStorage : null;
  } catch {
    return null;
  }
}
